import { useState } from "react";
import { useDispatch } from "react-redux";
import Logo from "../../assets/images/logo/linkologo.png";
import PrivateIcon from "../../assets/images/icon/private.png";
import LinkoappIcon from "../../assets/images/icon/linko-icon-logo.png";
import ModalIcon from "../../assets/images/icon/modal.png";
import { useNavigate } from "react-router-dom";
import LoginAnimation from "../../components/LoginAnimation";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toastError, toastSuccess } from "../../components/Toast";
import AuthService from "../../services/auth";
import { FaEye, FaEyeSlash, FaEnvelope, FaLock } from "react-icons/fa";
import { useLanguage } from "../../context/LanguageContext";
import localizeAuthError from "../../utils/localizeAuthError";
import { persistProfileToStorage } from "../../hooks/useSidebarData";
import { setUser } from "../../store/slices/userSlice";
import { initGlobalSocket } from "../../services/socket";
import { GoogleLogin } from "@react-oauth/google";

const LanguageToggle = ({ language, switchLanguage, height = "h-[42px]", minWidth = "min-w-[160px]" }) => {
  const base =
    "flex-1 h-full text-[14px] uppercase flex items-center justify-center px-5 whitespace-nowrap transition-colors font-roboto leading-none tracking-normal";
  return (
    <div
      className={`flex rounded-[22.5px] shadow-[inset_0_0_0_3px_#904DB0] ${height} bg-[#F8F7FC] ${minWidth} overflow-hidden`}
    >
      <button
        onClick={() => switchLanguage("es")}
        className={`${base} rounded-r-full ${language === "es"
          ? "bg-[#904DB0] text-[#F8F7FC] font-black"
          : "bg-transparent text-[#904DB0] font-semibold"
          }`}
      >
        ESP
      </button>
      <button
        onClick={() => switchLanguage("en")}
        className={`${base} rounded-l-full ${language === "en"
          ? "bg-[#904DB0] text-[#F8F7FC] font-black"
          : "bg-transparent text-[#904DB0] font-semibold"
          }`}
      >
        ENG
      </button>
    </div>
  );
};

const SignUp = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isShow, setIsShow] = useState(false);
  const [isSubmiting, setSubmiting] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const { language, switchLanguage, t } = useLanguage();

  const togglePasswordVisibility = () => setPasswordVisible(!passwordVisible);
  const toggleConfirmPasswordVisibility = () => setConfirmPasswordVisible(!confirmPasswordVisible);

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      setSubmiting(true);
      const response = await AuthService.googleLogin(
        credentialResponse.credential
      );
      if (response) {
        setIsShow(true);
        const userData = response?.data?.data;
        const token = response?.data?.token;
        try { localStorage.removeItem("linko_transcribe_result"); } catch (_e) { }
        try { indexedDB.deleteDatabase("linko_transcribe_db"); } catch (_e) { }
        localStorage.setItem("linko_web", token);
        persistProfileToStorage(userData);
        dispatch(setUser(userData));
        initGlobalSocket(dispatch);
        window.dispatchEvent(new Event("authChanged"));
        setTimeout(() => {
          setIsShow(false);
          toastSuccess(t.signUpSuccess || t.loginSuccess);
          if (userData?.is_subscription) {
            navigate("/chat");
          } else {
            navigate("/plans", { state: { tab: "plans" } });
          }
        }, 1000);
      }
    } catch (error) {
      toastError(localizeAuthError(error, t) || t.googleLoginError);
    } finally {
      setSubmiting(false);
    }
  };

  const handleSubmit = async (values) => {
    const email = values?.username.toLowerCase();
    try {
      setSubmiting(true);
      const response = await AuthService?.signup(email, values.password);
      if (response) {
        setIsShow(true);
        try {
          const loginRes = await AuthService.login({ email, password: values.password });
          if (loginRes?.data?.token) {
            try { localStorage.removeItem("linko_transcribe_result"); } catch (_e) { }
            try { indexedDB.deleteDatabase("linko_transcribe_db"); } catch (_e) { }
            const userData = loginRes.data.data;
            const token = loginRes.data.token;
            localStorage.setItem("linko_web", token);
            persistProfileToStorage(userData);
            dispatch(setUser(userData));
            initGlobalSocket(dispatch);
            window.dispatchEvent(new Event("authChanged"));
            setTimeout(() => {
              setIsShow(false);
              toastSuccess(t.signUpSuccess);
              if (userData?.is_subscription) {
                navigate("/chat");
              } else {
                navigate("/plans", { state: { tab: "plans" } });
              }
            }, 1000);
            return;
          }
        } catch (_loginErr) {
          console.error("Auto login after signup error:", _loginErr);
        }

        setTimeout(() => {
          setIsShow(false);
          toastSuccess(t.signUpSuccess);
          navigate("/login");
        }, 1000);
      }
    } catch (error) {
      setIsShow(false);
      toastError(localizeAuthError(error, t) || t.somethingWentWrong);
    } finally {
      setSubmiting(false);
    }
  };

  const formik = useFormik({
    initialValues: { username: "", password: "", confirmPassword: "" },
    validationSchema: Yup.object({
      username: Yup.string()
        .required(t.emailReq || "Email is required")
        .matches(
          /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/,
          t.emailInvalid || "Please enter a valid email address (e.g., name@domain.com)"
        ),
      password: Yup.string()
        .min(8, t.passwordMin || "Password must be at least 8 characters")
        .matches(/[A-Z]/, t.passwordStrong || "Password must contain at least one uppercase letter.")
        .matches(/[a-z]/, t.passwordStrong || "Password must contain at least one lowercase letter.")
        .matches(/[0-9]/, t.passwordStrong || "Password must contain at least one number.")
        .matches(/[^A-Za-z0-9]/, t.passwordStrong || "Password must contain at least one special character.")
        .required(t.passwordReq || "Password is required"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("password"), null], t.confirmMatch || "Passwords must match")
        .required(t.confirmReq || "Please confirm your password"),
    }),
    enableReinitialize: true,
    onSubmit: handleSubmit,
  });

  return (
    <>
      {isShow ? (
        <LoginAnimation />
      ) : (
        <div className="flex flex-col lg:flex-row min-h-screen lg:h-screen w-full font-roboto relative lg:overflow-hidden">

          {/* Language toggle - centered at the bottom of the screen (desktop only) */}
          <div className="hidden lg:block absolute bottom-2.5 lg:bottom-12 xl:bottom-12 2xl:bottom-16 left-1/2 -translate-x-1/2 z-20 auth-bottom-toggle">
            <LanguageToggle language={language} switchLanguage={switchLanguage} height="h-[32px] lg:h-[34px] xl:h-[36px] 2xl:h-[42px] auth-bottom-toggle-box" minWidth="min-w-[130px] lg:min-w-[135px] xl:min-w-[145px] 2xl:min-w-[160px]" />
          </div>

          {/* RIGHT SIDE — dark gradient with form */}
          <div className="w-full lg:w-1/2 lg:order-2 flex flex-col justify-between py-4 sm:py-6 lg:pt-6 lg:pb-3 xl:pt-6 xl:pb-3.5 2xl:py-8 px-4 sm:px-8 lg:px-6 xl:px-8 2xl:px-12 relative bg-[linear-gradient(157.06deg,#1A0E3E_46.27%,#904DB0_94.25%)] lg:h-full lg:overflow-y-auto overflow-x-hidden auth-outer-col-right">
            {/* Top spacing anchor to balance desktop top header */}
            <div className="hidden lg:block h-[36px] lg:h-[40px] xl:h-[44px] 2xl:h-[66px] shrink-0 auth-header" />

            {/* Sign Up card + language toggle below it */}
            <div className="w-full flex-1 flex flex-col items-center justify-center py-1 sm:py-2 lg:pb-14 xl:pb-14 2xl:pb-4 auth-content-wrapper">
              {/* Sign Up Card */}
              <div className="w-full max-w-[435px] sm:max-w-[455px] xl:max-w-[465px] 2xl:max-w-[500px] relative z-10 bg-[rgba(199,191,229,0.10)] rounded-[22px] sm:rounded-[27px] border border-[rgba(199,191,229,0.10)] px-6 sm:px-8 py-4 sm:py-5 lg:px-7 lg:py-5 xl:px-8 xl:py-5.5 2xl:px-10 2xl:py-7 flex flex-col auth-right-card auth-signup-card">
                <h2 className="text-[22px] sm:text-[24px] lg:text-[23px] xl:text-[25px] 2xl:text-[30px] font-extrabold text-white text-left w-full">
                  {t.heading}
                </h2>
                <form onSubmit={formik.handleSubmit} className="flex flex-col mt-2 sm:mt-2.5 2xl:mt-3" autoComplete="off">

                  {/* Input Fields Group */}
                  <div className="flex flex-col">
                    {/* Email */}
                    <div>
                      <div className="relative">
                        <FaEnvelope className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-[15px] pointer-events-none" />
                        <input
                          type="text"
                          name="username"
                          placeholder={t.emailPh || "Email address"}
                          className={`w-full h-[36px] sm:h-[38px] lg:h-[36px] xl:h-[38px] 2xl:h-[44px] bg-white rounded-[8px] focus:outline-none text-gray-800 font-medium placeholder-gray-500 pl-10 pr-4 text-[13px] lg:text-[13px] xl:text-[13.5px] 2xl:text-[14.5px] auth-right-input ${formik.touched.username && formik.errors.username ? "border-2 border-red-500" : ""
                            }`}
                          value={formik.values.username}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          autoComplete="off"
                        />
                      </div>
                      <div className="min-h-[14px] sm:min-h-[16px] mt-0.5">
                        {formik.touched.username && formik.errors.username && (
                          <div className="text-red-400 text-[10.5px] sm:text-[11px] leading-tight">{formik.errors.username}</div>
                        )}
                      </div>
                    </div>

                    {/* Password */}
                    <div>
                      <div className="relative">
                        <FaLock className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-[15px] pointer-events-none" />
                        <input
                          type={passwordVisible ? "text" : "password"}
                          name="password"
                          placeholder={t.passwordPh || "Password"}
                          className={`w-full h-[36px] sm:h-[38px] lg:h-[36px] xl:h-[38px] 2xl:h-[44px] bg-white rounded-[8px] focus:outline-none text-gray-800 font-medium placeholder-gray-500 pl-10 pr-10 text-[13px] lg:text-[13px] xl:text-[13.5px] 2xl:text-[14.5px] auth-right-input ${formik.touched.password && formik.errors.password ? "border-2 border-red-500" : ""
                            }`}
                          value={formik.values.password}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          autoComplete="new-password"
                        />
                        <button
                          type="button"
                          onClick={togglePasswordVisibility}
                          className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 text-base"
                        >
                          {passwordVisible ? <FaEye /> : <FaEyeSlash />}
                        </button>
                      </div>
                      <div className="min-h-[14px] sm:min-h-[16px] mt-0.5">
                        {formik.touched.password && formik.errors.password && (
                          <div className="text-red-400 text-[10.5px] sm:text-[11px] leading-tight">{formik.errors.password}</div>
                        )}
                      </div>
                    </div>

                    {/* Confirm Password */}
                    <div>
                      <div className="relative">
                        <FaLock className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-[15px] pointer-events-none" />
                        <input
                          type={confirmPasswordVisible ? "text" : "password"}
                          name="confirmPassword"
                          placeholder={t.confirmPasswordPh || "Confirm Password"}
                          className={`w-full h-[36px] sm:h-[38px] lg:h-[36px] xl:h-[38px] 2xl:h-[44px] bg-white rounded-[8px] focus:outline-none text-gray-800 font-medium placeholder-gray-500 pl-10 pr-10 text-[13px] lg:text-[13px] xl:text-[13.5px] 2xl:text-[14.5px] auth-right-input ${formik.touched.confirmPassword && formik.errors.confirmPassword ? "border-2 border-red-500" : ""
                            }`}
                          value={formik.values.confirmPassword}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          autoComplete="new-password"
                        />
                        <button
                          type="button"
                          onClick={toggleConfirmPasswordVisibility}
                          className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 text-base"
                        >
                          {confirmPasswordVisible ? <FaEye /> : <FaEyeSlash />}
                        </button>
                      </div>
                      <div className="min-h-[14px] sm:min-h-[16px] mt-0.5">
                        {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                          <div className="text-red-400 text-[10.5px] sm:text-[11px] leading-tight">{formik.errors.confirmPassword}</div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions Group */}
                  <div className="flex flex-col gap-2 mt-1 sm:mt-1.5">
                    <button
                      type="submit"
                      disabled={isSubmiting}
                      className="w-full h-[36px] sm:h-[38px] lg:h-[38px] xl:h-[40px] 2xl:h-[46px] bg-[#904DB0] hover:bg-[#7d3fa0] text-white font-semibold rounded-[87.5px] shadow-login-btn transition-colors flex justify-center items-center gap-[10.94px] text-[13.5px] lg:text-[14px] xl:text-[14.5px] 2xl:text-[15.5px] auth-right-btn !mt-0"
                    >
                      {isSubmiting ? (
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        t.submitBtn
                      )}
                    </button>

                    <div className="flex items-center gap-2.5 my-1.5 sm:my-2 2xl:my-2.5">
                      <div className="flex-1 h-px bg-white/20"></div>
                      <span className="text-white/80 text-[11px] font-medium tracking-wider">
                        {t.or}
                      </span>
                      <div className="flex-1 h-px bg-white/20"></div>
                    </div>

                    <div className="flex justify-center relative h-[36px] sm:h-[38px] lg:h-[38px] xl:h-[40px] 2xl:h-[44px] auth-google-btn-wrapper">
                      <button
                        type="button"
                        className="absolute inset-0 z-10 flex items-center justify-center gap-2 bg-white rounded-[8px] border border-gray-300 hover:bg-gray-50 transition-colors pointer-events-none"
                      >
                        <svg className="w-4 h-4 lg:w-4.5 lg:h-4.5 xl:w-5 xl:h-5 2xl:w-5 2xl:h-5 flex-shrink-0" viewBox="0 0 24 24">
                          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                        </svg>
                        <span className="text-gray-700 font-medium text-[12.5px] lg:text-[13px] xl:text-[13.5px] 2xl:text-[14.5px]">{t.googleSignUp || "Sign up with Google"}</span>
                      </button>
                      <div className="absolute inset-0 opacity-0">
                        <GoogleLogin
                          onSuccess={handleGoogleSuccess}
                          onError={() => toastError(t.googleLoginError)}
                          useOneTap={false}
                          type="standard"
                          size="large"
                          width="468"
                          text="signup_with"
                          shape="rectangular"
                        />
                      </div>
                    </div>

                    {/* Already have account */}
                    <div className="text-center text-[12px] xl:text-[13.5px] mt-1.5 sm:mt-2">
                      <button
                        type="button"
                        onClick={() => navigate("/login")}
                        className="bg-transparent border-none cursor-pointer text-white hover:text-[#D2C5E8] font-inherit hover:underline underline-offset-[4px] decoration-[#D2C5E8]/60"
                      >
                        {t.alreadyHave}
                      </button>
                    </div>
                  </div>

                </form>
              </div>

              {/* Language toggle - below the signup card (mobile/tablet only; desktop uses the bottom-center toggle) */}
              <div className="flex lg:hidden justify-center flex-shrink-0 mt-4">
                <LanguageToggle language={language} switchLanguage={switchLanguage} />
              </div>
            </div>
          </div>

          {/* LEFT SIDE — light bg with logo and content */}
          <div className="w-full lg:w-1/2 lg:order-1 flex flex-col justify-between relative bg-[#F7F8FD] px-4 sm:px-6 lg:px-6 xl:px-10 2xl:px-12 py-4 sm:py-6 lg:pt-6 lg:pb-3 xl:pt-6 xl:pb-3.5 2xl:py-8 lg:h-full overflow-hidden auth-outer-col-left">
            {/* Top header area for Logo and Back to App button */}
            <div className="w-full h-[36px] sm:h-[48px] lg:h-[40px] xl:h-[44px] 2xl:h-[66px] flex items-center justify-between shrink-0 z-20 auth-header">
              <div className="flex flex-col items-start gap-2">
                <img
                  src={Logo}
                  alt="Logo"
                  width="840"
                  height="348"
                  loading="eager"
                  decoding="sync"
                  className="w-[110px] sm:w-[150px] lg:w-[135px] xl:w-[150px] 2xl:w-[230px] h-auto object-contain auth-logo"
                  style={{ aspectRatio: "840/348" }}
                />
              </div>
            </div>

            {/* Main content vertically centered in remaining space */}
            <div className="w-full flex-1 flex flex-col justify-center items-center py-1 sm:py-2 lg:pb-14 xl:pb-14 2xl:pb-4 auth-content-wrapper">
              <div className="w-full flex flex-col justify-center gap-4 sm:gap-6 lg:gap-5 xl:gap-6 2xl:gap-8 max-w-[480px] xl:max-w-[520px] 2xl:max-w-[600px] mx-auto z-10 pt-1.5 sm:pt-4 lg:pt-0 pb-1.5 sm:pb-4 lg:pb-0 auth-container">
                <div className="w-full flex justify-center">
                  <div className="w-full max-w-[480px] xl:max-w-[520px] 2xl:max-w-[600px] flex flex-col items-start">
                    <h1
                      className={`text-left text-[#1A0E3E] leading-[1.15] font-light w-full ${language === "es"
                        ? "text-[22px] sm:text-[30px] lg:text-[31px] xl:text-[32px] 2xl:text-[44px] auth-left-title-es"
                        : "text-[24px] sm:text-[32px] lg:text-[34px] xl:text-[35px] 2xl:text-[48px] auth-left-title"
                        }`}
                    >
                      {t.leftTitle1}
                      <br />
                      <span className="font-bold text-[#3D2B90]">{t.leftTitle2}</span>
                    </h1>
                    <p className="text-left text-[#3D2B90] mt-2 sm:mt-3 lg:mt-3 xl:mt-3 2xl:mt-4 text-[13.5px] sm:text-[16px] lg:text-[15.5px] xl:text-[16px] 2xl:text-[20px] leading-[1.3] sm:leading-[1.35] lg:leading-[1.4] xl:leading-[1.4] 2xl:leading-[1.4] font-light w-full auth-left-subtitle">
                      {t.subtitle1}
                      <br />
                      {t.subtitle2}
                      <br />
                      {t.subtitle3}
                    </p>

                    {/* Start Free Trial button - commented out, empty placeholder keeps layout alignment */}
                    {/* <button
                      onClick={() => navigate("/login")}
                      className="w-full h-[38px] sm:h-[44px] lg:h-[42px] xl:h-[44px] 2xl:h-[54px] mt-6 sm:mt-8 lg:mt-11 xl:mt-12 2xl:mt-14 bg-[#1A0E3E] text-[#F7F7FF] rounded-[87.5px] font-bold text-[13px] sm:text-[14.5px] lg:text-[14.5px] xl:text-[15px] 2xl:text-[17.5px] leading-none uppercase hover:bg-[#281561] transition-colors flex items-center justify-center auth-trial-btn"
                    >
                      {t.trialBtn}
                    </button> */}
                    <div
                      aria-hidden="true"
                      className="w-full h-[38px] sm:h-[44px] lg:h-[42px] xl:h-[44px] 2xl:h-[54px] mt-6 sm:mt-8 lg:mt-11 xl:mt-12 2xl:mt-14 invisible pointer-events-none auth-trial-btn"
                    />
                  </div>
                </div>

                {/* 3 cards - same as Login (icon above centered text) */}
                <div className="flex items-stretch justify-center gap-2 sm:gap-2.5 lg:gap-2.5 xl:gap-3 2xl:gap-4 mt-2 sm:mt-4 lg:mt-2.5 xl:mt-3 2xl:mt-5 w-full max-w-[700px] auth-feature-cards">
                  {[
                    { icon: PrivateIcon, alt: "Private", title: t.card1Title, sub: t.card1Sub },
                    { icon: LinkoappIcon, alt: "Data Anonymization", title: t.card2Title, sub: t.card2Sub },
                    { icon: ModalIcon, alt: "No Model", title: t.card3Title, sub: t.card3Sub },
                  ].map(({ icon, alt, title, sub }) => (
                    <div
                      key={alt}
                      className="flex flex-col items-center justify-center gap-0.5 sm:gap-1 lg:gap-0.5 xl:gap-1 2xl:gap-2 bg-[#FFFFFF] rounded-[12px] sm:rounded-[24px] shadow-[0px_0px_23.6px_-11px_#C7BFE4] flex-1 h-[78px] sm:h-[105px] lg:h-[88px] xl:h-[92px] 2xl:h-[145px] px-1 sm:px-2.5 lg:px-2 xl:px-2.5 2xl:px-3 py-1 sm:py-2 lg:py-1.5 xl:py-2 2xl:py-3 min-w-0 overflow-hidden auth-feature-card"
                    >
                      <img
                        src={icon}
                        alt={alt}
                        className="h-[22px] sm:h-[30px] lg:h-[26px] xl:h-[28px] 2xl:h-[52px] w-[22px] sm:w-[30px] lg:w-[26px] xl:w-[28px] 2xl:w-[52px] object-contain shrink-0"
                      />
                      <div className="flex flex-col items-center text-center min-w-0">
                        <span className="text-[#1A0E3E] font-bold text-[9.5px] sm:text-[11.5px] lg:text-[11px] xl:text-[11.5px] 2xl:text-[17px] leading-tight auth-feature-card-title">
                          {title}
                        </span>
                        <span className="text-[#1A0E3E] font-normal text-[8.5px] sm:text-[10px] lg:text-[9.5px] xl:text-[10px] 2xl:text-[15px] leading-tight mt-0.5 auth-feature-card-sub">
                          {sub}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SignUp;